import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { SEO_PAGE_HEADERS } from "~/lib/cache.server"
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { serviceCategories, professionalProfiles, professionalCategories, geoFrance } from '@afficheservices/db/schema'
import { eq, and, sql, desc, isNull } from 'drizzle-orm'
import { assertCityNotDomTom } from '~/lib/dom-tom.server'
import { assertCanonicalCitySlug } from '~/lib/canonical-slug.server'
import { citySlugify, findGeoCity } from '~/lib/geo-city.server'
import { prosNearCity } from '~/lib/pros-near-city.server'
import { getNeighborCities } from '~/lib/city-data.server'
import { seoMeta } from '~/lib/seo-meta'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { FloatingCTA } from '~/components/floating-cta'
import { parseServiceCitySlug, generatePageContent, SERVICE_LABELS, SERVICE_PRICES, EMERGENCY_SERVICES, SERVICE_PHOTOS, artisanSlug } from '~/lib/programmatic-seo'
import { ProgressivePicture } from '~/components/progressive-picture'
import { AnswerBlock } from '~/components/answer-block'
import { generateCityServiceContent, fetchCityContext } from '~/lib/city-content'
import { fetchCityServiceStats } from '~/lib/city-service-stats.server'
import { PriceEstimator } from '~/components/price-estimator'
import { RgeCrossLink } from '~/components/rge-cross-link'
import { InlineQuiz } from '~/components/inline-quiz'
import { getSeoContent } from '~/lib/seo-content.server'
import { formatRating } from '~/lib/format'
import { LeadCaptureBlock, CallbackForm, SeoExitIntent } from '~/components/lead-capture-block'
import { GeoAnswerBlock, SiloLinks } from '~/components/geo-answer'
import { AuthorByline, AuthorSchema, getAuthorForCategory } from '~/components/eeat-author'
import { ArticleMeta, ArticleSchema, HowToSchema, ComparisonTable } from '~/components/seo-content'
import { ContentMeta, SourceCitations, InternaunteQuestions } from '~/components/content-trust'
import { FAQSchema, BreadcrumbSchema } from '~/components/structured-data'
import {
  Star, CheckCircle, ArrowRight, Euro,
  Calculator, Clock,
} from 'lucide-react'

export async function loader({ params }: LoaderFunctionArgs) {
  const { slug } = params
  // Collapse accented / uppercase variants onto the ASCII URL before doing any
  // work — otherwise both spellings render 200 with their own self-canonical.
  assertCanonicalCitySlug(slug ?? '', (c) => `/prix/${c}`)
  const parsed = parseServiceCitySlug(slug ?? '')

  if (!parsed) throw new Response('Page non trouvée', { status: 404 })

  const { serviceSlug, cityName } = parsed
  await assertCityNotDomTom(cityName)

  // "/prix/climaticien-france" is a deliberate national page; every other value
  // must be a real commune. Without this check "/prix/plombier-zzzznotacity"
  // returned 200 with 2 800 words, i.e. an unbounded supply of soft-404s for
  // Googlebot to discover and re-crawl.
  const citySlug = citySlugify(cityName)
  const isNational = citySlug === 'france'
  const geoCity = isNational ? null : await findGeoCity(citySlug)
  if (!isNational && !geoCity) throw new Response('Page non trouvée', { status: 404 })

  const content = generatePageContent('prix', serviceSlug, cityName)
  const svcInfo = SERVICE_LABELS[serviceSlug]
  const price = SERVICE_PRICES[serviceSlug]

  // Find pros
  const [cat] = await db
    .select({ id: serviceCategories.id, name: serviceCategories.nameFr, slug: serviceCategories.slug })
    .from(serviceCategories)
    .where(eq(serviceCategories.slug, serviceSlug))
    .limit(1)

  // The pro list is the only genuinely local block on this page. It used to be
  // filtered by category alone, so /prix/plombier-nantes and /prix/plombier-lille
  // listed the identical six artisans (none of them in either city). City first,
  // department as fallback, empty when neither has anyone.
  let pros: any[] = []
  if (cat && geoCity) {
    pros = await prosNearCity({
      categoryId: cat.id,
      city: citySlug,
      departmentCode: geoCity.dept,
      limit: 6,
    })
  } else if (cat) {
    // National page — a country-wide top-rated list is the correct answer here.
    pros = await db
      .select({
        companyName: professionalProfiles.companyName,
        slug: professionalProfiles.slug,
        city: professionalProfiles.city,
        ratingAvg: professionalProfiles.ratingAvg,
        reviewCount: professionalProfiles.reviewCount,
        identityVerified: professionalProfiles.identityVerified,
      })
      .from(professionalProfiles)
      .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
      .where(and(
        eq(professionalCategories.categoryId, cat.id),
        eq(professionalProfiles.identityVerified, true),
      ))
      .orderBy(desc(professionalProfiles.ratingAvg))
      .limit(6)
  }

  // Real neighbours, distance-ranked. RANDOM() linked Nantes to Beuvrages,
  // Chauvigny and Thann — useless to a reader, and a link graph that changed on
  // every cache fill, which is how Googlebot kept finding fresh noindex /prix/
  // URLs that are in no sitemap.
  const relatedCities = geoCity
    ? await getNeighborCities(Number(geoCity.lat), Number(geoCity.lon), geoCity.communeCode ?? '', 8, 40)
    : await db
        .select({ name: geoFrance.communeName })
        .from(geoFrance)
        .orderBy(sql`${geoFrance.population} DESC NULLS LAST`)
        .limit(8)

  // Stable ordering — a sibling-service list that reshuffles per request is a
  // different internal link graph on every crawl.
  const relatedServices = await db
    .select({ name: serviceCategories.nameFr, slug: serviceCategories.slug })
    .from(serviceCategories)
    .where(and(isNull(serviceCategories.parentId), sql`${serviceCategories.slug} != ${serviceSlug}`))
    .orderBy(serviceCategories.displayOrder)
    .limit(5)

  // FAQs
  const faqs = [
    { question: `Combien coûte ${svcInfo?.article ?? 'un'} ${svcInfo?.artisan ?? serviceSlug} à ${cityName} ?`, answer: price ? `Les tarifs à ${cityName} varient de ${price.min} € à ${price.max} € par ${price.unit}. Le prix dépend du type de travaux, de la complexité et de l'accessibilité du chantier.` : `Les prix varient selon le type de travaux. Demandez des devis gratuits pour obtenir un prix exact.` },
    { question: `Comment trouver le meilleur ${svcInfo?.artisan ?? 'artisan'} à ${cityName} ?`, answer: `Comparez les devis de 3 professionnels vérifiés sur AfficheServices. Vérifiez les avis clients, l'assurance RC Pro et l'expérience avant de choisir.` },
    { question: 'Le devis est-il gratuit ?', answer: 'Oui, le service est 100% gratuit et sans engagement. Vous recevez jusqu\'à 3 devis personnalisés sous 48h.' },
  ]

  const stats = cat ? await fetchCityServiceStats(cat.id, cityName) : null

  return {
    content,
    serviceSlug,
    cityName,
    slug: slug ?? '',
    svcInfo,
    price,
    pros,
    relatedCities: relatedCities.map((c) => c.name),
    relatedServices,
    faqs,
    richContent: generateCityServiceContent(serviceSlug, cityName, await fetchCityContext(citySlug)),
    aiContent: await getSeoContent(serviceSlug, cityName),
    stats,
    // Reciprocal link to the /services directory page for the same (service,
    // city) — signals the two URLs are a complementary pair, not duplicates.
    servicesUrl: (() => {
      const noun = artisanSlug(serviceSlug)
      const s = slug ?? ''
      const cityPart = s.startsWith(noun + '-') ? s.slice(noun.length + 1) : ''
      return cityPart ? `/services/${serviceSlug}/${cityPart}` : null
    })(),
  }
}

export function headers() { return SEO_PAGE_HEADERS }

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  // Noindex pages without DB-backed unique AI content (avoid duplicate content
  // across thousands of programmatic variants). Only index when we have real
  // city-specific expertIntro/localContext/priceAnalysis.
  return seoMeta({
    // De-cannibalize the /prix vs /services pair (they share the seo_content
    // body but serve different intents — price research vs hire a pro): keep
    // the PRICE-angled template title here so it diverges from the /services
    // directory page, which keeps the seo_content meta_title.
    title: data?.content?.title ?? data?.aiContent?.metaTitle ?? 'Prix travaux',
    description: data?.content?.description ?? data?.aiContent?.metaDescription ?? '',
    path: `/prix/${data?.slug ?? ''}`,
    imageTitle: data?.content?.h1 ?? data?.content?.title,
    imageSubtitle: 'Prix réels & 3 devis gratuits sous 48h',
    noindex: !data?.aiContent,
  })
}

export default function PrixPage() {
  const { content, serviceSlug, cityName, slug, svcInfo, price, pros, relatedCities, relatedServices, faqs, richContent, aiContent, stats, servicesUrl } = useLoaderData<typeof loader>()
  const citySlug = cityName.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-")

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <FAQSchema items={aiContent?.faqItems ? aiContent.faqItems.map(f => ({ question: f.q, answer: f.a })) : faqs} />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Prix', url: 'https://www.afficheservices.fr/guide-prix' },
        { name: content.h1, url: `https://www.afficheservices.fr/prix/${slug}` },
      ]} />
      <ArticleSchema title={content.title ?? ""} description={content.description ?? ""} url={`https://www.afficheservices.fr/prix/${slug}`} />
      <HowToSchema name={`Comment obtenir un devis ${svcInfo?.name?.toLowerCase() ?? "travaux"} à ${cityName}`} steps={richContent.howItWorks} />
      <AuthorSchema author={getAuthorForCategory(serviceSlug)} articleUrl={`https://www.afficheservices.fr/prix/${slug}`} />

      <div className="mx-auto max-w-4xl px-4 py-10 lg:px-6">
        {/* Author byline — E-E-A-T signal */}
        <AuthorByline author={getAuthorForCategory(serviceSlug)} readTime={8} />

        {/* GEO Answer-first block — for ChatGPT/Perplexity citation */}
        <div className="mt-4">
          <GeoAnswerBlock
            question={`Combien coûte ${svcInfo?.name?.toLowerCase() ?? 'un artisan'} à ${cityName} en 2026 ?`}
            answer={`Le prix ${svcInfo?.name?.toLowerCase() ?? 'des travaux'} à ${cityName} varie entre ${price?.min ?? 50} et ${price?.max ?? 200} € ${price?.unit ? `par ${price.unit}` : ''} en 2026, main d'œuvre comprise. Ce tarif dépend de la complexité du projet, des matériaux choisis et de l'urgence de l'intervention.`}
            details={[
              `Prix moyen constaté à ${cityName} : ${price ? `${price.min}–${price.max} €/${price.unit}` : 'variable selon projet'}`,
              'Devis gratuit et sans engagement sous 48h',
              'Artisans vérifiés avec assurance décennale',
              'Jusqu\'à 3 devis comparatifs pour choisir',
            ]}
            priceRange={price ? { min: price.min, max: price.max, unit: price.unit ?? 'unité' } : undefined}
            ctaUrl={`/demande-devis/1?service=${serviceSlug}`}
          />
        </div>

        {/* Crisis banner — emergency services only (24/7 intent capture) */}
        {EMERGENCY_SERVICES.includes(serviceSlug) && (
          <Link
            to={`/urgence/${slug}`}
            className="mt-4 flex items-center justify-between gap-3 rounded-xl border-2 border-red-300 bg-gradient-to-r from-red-50 to-orange-50 px-4 py-3 transition-colors hover:from-red-100 hover:to-orange-100"
          >
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
              </span>
              <div className="text-sm">
                <strong className="text-red-700">Urgence {(svcInfo?.name ?? serviceSlug).toLowerCase()} ?</strong>
                <span className="ml-1 text-red-600">Intervention 24/7 à {cityName} →</span>
              </div>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-red-500" />
          </Link>
        )}

        {/* Header */}
        <div className="relative mt-4 overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-blue-600 p-8 text-white">
          <img
            src={`/api/og?title=${encodeURIComponent(content.h1 ?? `Prix ${svcInfo?.name ?? 'travaux'}`)}&subtitle=${encodeURIComponent(`Tarifs ${cityName} · Devis gratuit`)}`}
            alt={`Prix ${svcInfo?.name ?? 'travaux'} à ${cityName}`}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-overlay"
            loading="eager"
            width={1200}
            height={630}
          />
          <div className="relative">
            <div className="flex flex-wrap items-center gap-2 text-sm text-blue-200">
              <span className="inline-flex items-center gap-1"><Euro className="h-4 w-4" /> Guide des prix {new Date().getFullYear()}</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white backdrop-blur-sm">
                <Clock className="h-3 w-3" /> Mis à jour : {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </span>
            </div>
            <h1 className="mt-2 text-2xl font-bold sm:text-3xl">{content.h1}</h1>
            <p className="mt-3 text-sm leading-relaxed text-blue-100">{content.intro}</p>
            {price && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white/20 px-4 py-2 backdrop-blur-sm">
                <Calculator className="h-4 w-4" />
                <span className="text-sm font-semibold">{price.min}–{price.max} € / {price.unit}</span>
              </div>
            )}
            {servicesUrl && (
              <div className="mt-3">
                <Link to={servicesUrl} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-100 underline-offset-2 hover:underline">
                  Trouver {svcInfo?.article ?? 'un'} {svcInfo?.artisan ?? serviceSlug} vérifié à {cityName} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Inline interactive price estimator */}
        {price && (
          <PriceEstimator
            serviceName={svcInfo?.name ?? serviceSlug}
            artisanName={svcInfo?.artisan ?? serviceSlug}
            cityName={cityName}
            serviceSlug={serviceSlug}
            citySlug={citySlug}
            unit={price.unit ?? 'unité'}
            basePrice={{ min: price.min, max: price.max }}
            priceBand={stats?.priceBand ?? null}
          />
        )}

        {/* Live demand signal — proprietary data block (SXO Persona C fix) */}
        {stats && stats.recentLeads >= 5 && (
          <p className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            📊 <strong>{stats.recentLeads} demandes de devis</strong> ont été
            envoyées à des {svcInfo?.artisan ?? 'artisan'}s à {cityName} ces 30 derniers jours
            {stats.avgResponseHours !== null && stats.avgResponseHours > 0 && !isNaN(stats.avgResponseHours) ? (
              <> · réponse moyenne <strong>{stats.avgResponseHours < 1 ? `${Math.round(stats.avgResponseHours * 60)} min` : `${Math.round(stats.avgResponseHours)}h`}</strong></>
            ) : null}
            {stats.proCount > 0 ? (
              <> · <strong>{stats.proCount} {svcInfo?.artisan ?? 'artisan'}s vérifiés</strong> dans la zone</>
            ) : null}
          </p>
        )}

        {/* RGE cross-link — Persona D (RGE seeker) fix */}
        <RgeCrossLink
          serviceSlug={serviceSlug}
          cityName={cityName}
          citySlug={citySlug}
          serviceName={svcInfo?.name ?? serviceSlug}
        />

        {/* Price breakdown */}
        {price && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">Grille tarifaire — {svcInfo?.name ?? serviceSlug}</h2>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div className="rounded-xl bg-emerald-50 p-4 text-center">
                <p className="text-xs text-emerald-600">Prix minimum</p>
                <p className="mt-1 text-2xl font-bold text-emerald-700">{price.min} €</p>
                <p className="text-[10px] text-emerald-500">par {price.unit}</p>
              </div>
              <div className="rounded-xl bg-primary-50 p-4 text-center">
                <p className="text-xs text-primary">Prix moyen</p>
                <p className="mt-1 text-2xl font-bold text-primary">{Math.round((price.min + price.max) / 2)} €</p>
                <p className="text-[10px] text-blue-500">par {price.unit}</p>
              </div>
              <div className="rounded-xl bg-amber-50 p-4 text-center">
                <p className="text-xs text-amber-600">Prix maximum</p>
                <p className="mt-1 text-2xl font-bold text-amber-700">{price.max} €</p>
                <p className="text-[10px] text-amber-500">par {price.unit}</p>
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-400">* Prix indicatifs TTC à {cityName}, mars 2026</p>
          </div>
        )}

        {/* Article Meta */}
        <div className="mt-4 space-y-3">
          <ArticleMeta />
        </div>

        {/* Secondary low-commit CTA — single express-quote entry kept for
            visitors not ready for the full /demande-devis funnel. Primary
            funnel remains /demande-devis/1 everywhere else on the page. */}
        <div className="mt-4 text-center">
          <Link
            to={`/devis-gratuit?service=${serviceSlug}`}
            className="inline-flex min-h-12 items-center gap-1.5 px-4 py-3 text-sm font-medium text-slate-500 underline-offset-2 hover:text-primary hover:underline"
          >
            Estimation rapide en 30 secondes
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>

        {/* AI citation block — single 134-167w passage for ChatGPT/Perplexity */}
        {aiContent && (
          <AnswerBlock
            question={`Combien coûte ${svcInfo?.article ?? 'un'} ${svcInfo?.artisan ?? serviceSlug} à ${cityName} en ${new Date().getFullYear()} ?`}
            parts={[
              aiContent.expertIntroParas[0],
              aiContent.priceAnalysis,
              aiContent.localContextParas[0],
            ]}
          />
        )}

        {/* AI-optimized expert content (unique per city+service) */}
        {aiContent ? (
          <article className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <ContentMeta cityName={cityName} serviceName={svcInfo?.name ?? 'Travaux'} />
            <h2 className="mt-3 text-lg font-bold text-slate-900">Comment se calculent les prix {svcInfo?.name.toLowerCase() ?? 'travaux'} à {cityName} ?</h2>
            <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
              {aiContent.expertIntroParas.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <SourceCitations serviceSlug={serviceSlug} />
          </article>
        ) : (
          <article className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">Comment se calculent les prix {svcInfo?.name.toLowerCase() ?? 'travaux'} à {cityName} ?</h2>
            <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
              {richContent.introParas.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </article>
        )}

        {/* AI local context */}
        {aiContent?.localContextParas && (
          <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-50 p-6">
            <h3 className="text-base font-bold text-blue-900">Spécificités locales — {cityName}</h3>
            <div className="mt-3 space-y-2 text-sm leading-relaxed text-blue-800">
              {aiContent.localContextParas.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
        )}

        {/* AI price analysis */}
        {aiContent?.priceAnalysis && (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
            <h3 className="text-base font-bold text-emerald-900">Analyse des prix — {svcInfo?.name ?? 'Travaux'} à {cityName}</h3>
            <p className="mt-3 text-sm leading-relaxed text-emerald-800">{aiContent.priceAnalysis}</p>
          </div>
        )}

        {/* Detailed pricing table */}
        {richContent.pricing.rows.length > 0 && (
          <div className="mt-6 rounded-2xl border border-border bg-white shadow-xs overflow-hidden">
            <div className="border-b border-border px-6 py-4">
              <h2 className="text-lg font-bold text-slate-900">Grille tarifaire détaillée — {cityName}</h2>
              <p className="mt-1 text-xs text-slate-500">{richContent.pricingIntro}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-2.5">Prestation</th>
                    <th className="px-5 py-2.5 text-right">Min</th>
                    <th className="px-5 py-2.5 text-right">Max</th>
                    <th className="hidden px-5 py-2.5 sm:table-cell">Détail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {richContent.pricing.rows.map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-5 py-3 text-sm font-medium text-slate-900">{row.service}</td>
                      <td className="px-5 py-3 text-right text-sm font-semibold text-emerald-700">{row.priceMin} €</td>
                      <td className="px-5 py-3 text-right text-sm font-semibold text-slate-900">{row.priceMax} €</td>
                      <td className="hidden px-5 py-3 text-xs text-slate-500 sm:table-cell">{row.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-border bg-slate-50 px-5 py-2.5 text-[10px] text-slate-400">
              * Prix indicatifs TTC à {cityName}, mars 2026
            </div>
          </div>
        )}

        {/* Template sections — only shown when NO AI content to avoid duplication */}
        {!aiContent && (
          <>
            {/* Price factors */}
            <div className="mt-6 rounded-2xl border border-border bg-white p-6 shadow-xs">
              <h3 className="text-base font-bold text-slate-900">Ce qui influence le prix à {cityName}</h3>
              <ul className="mt-4 space-y-2.5">
                {richContent.pricing.factors.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-700">{i + 1}</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* How to save */}
            <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
              <h3 className="text-base font-bold text-emerald-900">Comment économiser à {cityName}</h3>
              <ul className="mt-3 space-y-2">
                {richContent.pricing.savings.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-emerald-800">
                    <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            {/* Why choose us */}
            <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
              <h2 className="text-lg font-bold text-slate-900">Pourquoi passer par AfficheServices à {cityName} ?</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {richContent.whyPoints.map((p, i) => (
                  <div key={i} className="rounded-xl bg-slate-50 p-4">
                    <h4 className="text-sm font-semibold text-slate-900">{p.title}</h4>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">{p.text}</p>
                  </div>
                ))}
              </div>
            </div>

          </>
        )}

        {/* Step by step — always rendered to match HowTo schema. Schema/HTML
            parity helps AI extractors (ChatGPT/Perplexity) tie the structured
            steps to visible content. Without this, HowTo schema declares
            steps that don't appear in HTML — inconsistency penalty. */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Comment obtenir un devis à {cityName} ?</h2>
          <ol className="mt-4 space-y-4 list-none p-0">
            {richContent.howItWorks.map((s) => (
              <li key={s.step} className="flex gap-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">{s.step}</div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-900">{s.title}</h4>
                  <p className="mt-0.5 text-xs text-slate-500">{s.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Expert tips — AI or template */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Quels conseils pour choisir un {svcInfo?.artisan ?? 'artisan'} à {cityName} ?</h2>
          <div className="mt-4 space-y-3">
            {(aiContent?.expertTips ?? richContent.tips).map((tip, i) => (
              <div key={i} className="rounded-xl bg-slate-50 p-4">
                <h4 className="text-sm font-semibold text-slate-900">{tip.title}</h4>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Local info — only if NO AI content (AI has its own "Spécificités locales" block) */}
        {!aiContent && (
          <div className="mt-6 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">Quelles spécificités locales pour les travaux de {svcInfo?.name?.toLowerCase() ?? 'travaux'} à {cityName} ?</h2>
            <div className="mt-3 space-y-2 text-sm leading-relaxed text-slate-600">
              {richContent.localInfo.map((info, i) => <p key={i}>{info.replace(/\*\*/g, '')}</p>)}
            </div>
          </div>
        )}

        {/* Pros in this city */}
        {pros.length > 0 && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">{svcInfo?.name ?? 'Professionnels'} à {cityName}</h2>
            <div className="mt-4 space-y-3">
              {pros.map((pro) => {
                const photo = SERVICE_PHOTOS[serviceSlug]
                return (
                <Link key={pro.slug} to={`/pro/${pro.slug}`}
                  className="flex items-center gap-3 rounded-xl bg-slate-50 p-3 transition-colors hover:bg-primary-50">
                  {photo ? (
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg">
                      <ProgressivePicture
                        baseUrl={photo}
                        alt={`${svcInfo?.artisan ?? serviceSlug} ${cityName} — ${pro.companyName}`}
                        width={96}
                        height={96}
                        sizes="48px"
                      />
                    </div>
                  ) : (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold text-primary">
                      {pro.companyName[0]}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">{pro.companyName}</span>
                      {pro.identityVerified && <CheckCircle className="h-3.5 w-3.5 text-primary" />}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {formatRating(pro.ratingAvg)} ({pro.reviewCount})
                      <span>·</span> {pro.city}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-300" />
                </Link>
                )
              })}
            </div>
          </div>
        )}

        {/* Comparison */}
        <div className="mt-8">
          <ComparisonTable serviceName={svcInfo?.name ?? "Service"} />
        </div>

        {/* Questions des internautes — UGC-style social proof */}
        {aiContent && (
          <InternaunteQuestions serviceSlug={serviceSlug} cityName={cityName} artisan={svcInfo?.artisan ?? 'artisan'} />
        )}

        {/* FAQ accordion — only if NO AI content (AI has visible FAQ block below) */}
        {!aiContent && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">Questions fréquentes</h2>
            <div className="mt-4 space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="group border-b border-border pb-4 last:border-0">
                  <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-800 hover:text-primary">
                    {faq.question}
                    <span className="text-slate-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-2 text-sm text-slate-600">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        )}

        {/* Internal links */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-white p-4 shadow-xs">
            <h3 className="text-xs font-semibold text-slate-500">Prix dans d'autres villes</h3>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {relatedCities.map((city) => {
                const artisanSlug = (svcInfo?.artisan ?? serviceSlug).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
                const citySlug = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
                return (
                  <Link key={city} to={`/prix/${artisanSlug}-${citySlug}`}
                    className="rounded-full border border-border px-2.5 py-1 text-[11px] text-slate-600 hover:border-primary hover:text-primary">
                    Prix {svcInfo?.artisan} {city}
                  </Link>
                )
              })}
            </div>
          </div>
          <div className="rounded-xl border border-border bg-white p-4 shadow-xs">
            <h3 className="text-xs font-semibold text-slate-500">Autres services à {cityName}</h3>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {relatedServices.map((rs) => {
                const rsLabel = SERVICE_LABELS[rs.slug]
                const artisanSlug = (rsLabel?.artisan ?? rs.slug).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
                const citySlug = cityName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
                return (
                  <Link key={rs.slug} to={`/prix/${artisanSlug}-${citySlug}`}
                    className="rounded-full border border-border px-2.5 py-1 text-[11px] text-slate-600 hover:border-primary hover:text-primary">
                    Prix {rsLabel?.artisan ?? rs.name} {cityName}
                  </Link>
                )
              })}
            </div>
          </div>
        </div>

        {/* Quiz */}
        <div className="mt-8">
          <InlineQuiz serviceName={svcInfo?.name ?? 'Service'} cityName={cityName} categorySlug={serviceSlug} />
        </div>

        {/* Lead capture */}
        <div className="mt-8">
          <LeadCaptureBlock serviceName={svcInfo?.name ?? 'Service'} cityName={cityName} categorySlug={serviceSlug} />
        </div>

        {/* Callback */}
        <div className="mt-6">
          <CallbackForm serviceName={svcInfo?.name ?? 'Service'} cityName={cityName} categorySlug={serviceSlug} />
        </div>

        {/* Semantic silo links — AI-enhanced or manual */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <SiloLinks title={`${svcInfo?.name ?? 'Service'} — Pages liées`} links={
            aiContent?.contextLinks?.slice(0, 6) ?? [
              { href: `/urgence/${slug}`, label: `Urgence ${svcInfo?.artisan ?? 'artisan'} ${cityName}` },
              { href: `/guide-prix/${serviceSlug}`, label: `Guide prix ${svcInfo?.name?.toLowerCase() ?? 'travaux'}` },
            ]
          } />
          <SiloLinks title="Autres villes" links={
            relatedCities.slice(0, 5).map((city) => {
              const artisanSlug = (svcInfo?.artisan ?? serviceSlug).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
              const citySlug = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
              return { href: `/prix/${artisanSlug}-${citySlug}`, label: `Prix ${svcInfo?.artisan ?? 'artisan'} ${city}` }
            })
          } />
        </div>

        {/* Visible FAQ — AI content preferred, template fallback */}
        <div className="mt-8">
          <h2 className="text-lg font-bold text-slate-900">Questions fréquentes</h2>
          <div className="mt-4 space-y-4">
            {aiContent?.faqItems ? (
              aiContent.faqItems.map((faq, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="text-sm font-semibold text-slate-900">{faq.q}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{faq.a}</p>
                </div>
              ))
            ) : (
              faqs.map((faq, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="text-sm font-semibold text-slate-900">{faq.question}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <SeoExitIntent serviceName={svcInfo?.name ?? 'Service'} cityName={cityName} categorySlug={serviceSlug} />
      <FloatingCTA
        href={`/demande-devis/1?service=${serviceSlug}`}
        label={`Estimer ${svcInfo?.name?.toLowerCase() ?? 'mon prix'} à ${cityName}`}
        subtitle="3 devis gratuits comparatifs · 48h"
        whatsappMessage={`Bonjour, je souhaite obtenir un devis ${svcInfo?.name?.toLowerCase() ?? ''} à ${cityName}.`}
      />
      <Footer />
    </div>
  )
}
